import { useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listarAberturas } from '../api/aberturas.js';
import { useConsulta } from '../api/useConsulta.js';
import { Abas, PainelDeAba } from '../componentes/Abas.js';
import type { Aba } from '../componentes/Abas.js';
import { Pagina } from '../componentes/Pagina.js';
import { AbaDivergencias } from './movimentacoes/AbaDivergencias.js';
import { AbaDuasEtapas } from './movimentacoes/AbaDuasEtapas.js';
import { AbaEtapaUnica } from './movimentacoes/AbaEtapaUnica.js';
import './Movimentacoes.css';

const GRUPO = 'movimentacoes';

const DUAS_ETAPAS = 'duas-etapas';
const DIVERGENCIAS = 'divergencias';
const ETAPA_UNICA = 'etapa-unica';

const IDS_DAS_ABAS: readonly string[] = [DUAS_ETAPAS, DIVERGENCIAS, ETAPA_UNICA];

/** A aba vive na URL: o supervisor manda o link das divergências para quem decide. */
function abaDaUrl(valor: string | null): string {
  return valor !== null && IDS_DAS_ABAS.includes(valor) ? valor : DUAS_ETAPAS;
}

/**
 * Quantas divergências esperam decisão no armazém inteiro — o `total` do
 * filtro, com página de um item só. Sem resposta, o selo não aparece: selo
 * chutado ensina a ignorar os outros.
 */
function useDivergenciasPendentes() {
  const buscar = useCallback(() => listarAberturas({ status: 'EmDivergencia', tamanho: 1 }), []);
  const { dados, recarregar } = useConsulta(buscar);
  return { total: dados?.total ?? null, recarregar };
}

/**
 * Movimentações (RF-076, UC08, r1-mov).
 *
 * Três abas, porque são três perguntas:
 *
 * - **Em duas etapas** — o que foi iniciado no coletor e quanto falta entregar,
 *   com o status das entregas no ERP;
 * - **Divergências pendentes** — a entrega que passou da quantidade iniciada,
 *   retida até alguém decidir;
 * - **Etapa única** — o registro do app anterior, que ainda chega de aparelho
 *   não atualizado. Continua acessível e continua só leitura.
 */
export function Movimentacoes() {
  const [parametros, definirParametros] = useSearchParams();
  const aba = abaDaUrl(parametros.get('aba'));
  const divergencias = useDivergenciasPendentes();

  const abas: readonly Aba[] = [
    { id: DUAS_ETAPAS, rotulo: 'Em duas etapas' },
    {
      id: DIVERGENCIAS,
      rotulo: 'Divergências pendentes',
      ...(divergencias.total === null ? {} : { contador: divergencias.total }),
    },
    { id: ETAPA_UNICA, rotulo: 'Etapa única (app anterior)' },
  ];

  return (
    <Pagina
      contexto="Movimentações"
      titulo="Movimentações"
      acoes={
        <Link className="movimentacoes__acao" to="/movimentacoes/produto">
          Rastrear produto
        </Link>
      }
      faixa={
        <div className="movimentacoes__faixa">
          <Abas
            rotulo="Movimentações"
            grupo={GRUPO}
            abas={abas}
            ativa={aba}
            aoTrocar={(id) => {
              definirParametros(id === DUAS_ETAPAS ? {} : { aba: id });
            }}
          />
        </div>
      }
    >
      <PainelDeAba id={aba} grupo={GRUPO}>
        {aba === DUAS_ETAPAS && <AbaDuasEtapas />}
        {aba === DIVERGENCIAS && <AbaDivergencias aoDecidir={divergencias.recarregar} />}
        {aba === ETAPA_UNICA && <AbaEtapaUnica />}
      </PainelDeAba>
    </Pagina>
  );
}
